import FilterPanel from 'components/filter/FilterPanel';
import StyledInput from 'components/styledInput/StyledInput';
import { buildSupplierFilterOptions } from 'constants/missionSupplierFilters';
import useText from 'hooks/useText';
import { NO_FILTERS, activeFilterCount, departmentLabel } from 'pages/companies/companiesItems';
import { commercialsOf } from './companyAssignees';

const NO_DEPARTMENT = 'other';

/**
 * The filters of the companies in the admin's « Plus de filtres » panel. The
 * real ones (department, category, supplier, missions, pay conditions) go to
 * admin/crm/companies with « Action à faire » and « Commercial » (one of the
 * Commercials, 510). `options` holds the real choices
 * (hooks/useCompanyFilterOptions), `team` the CRM team.
 */
const CompanyFilterPanel = ({ isOpen, onClose, filters, onChange, options, team, meId }) => {
  const { text } = useText('companies');
  const { departments, categories, suppliers } = options;

  // The api spells a department several ways (« MARTINIQUE », « Martinique »);
  // it matches them in small letters.
  const departmentList = [
    ...new Map(departments.map((department) => [department.toLowerCase(), departmentLabel(department)])),
  ].map(([value, label]) => ({ value, label, checked: filters.departments.includes(value) }));
  departmentList.push({
    value: NO_DEPARTMENT,
    label: text('noDepartment'),
    checked: filters.departments.includes(NO_DEPARTMENT),
  });
  const supplierList = buildSupplierFilterOptions(suppliers).map((option) => ({
    value: option.value,
    label: option.name,
  }));
  const person = (id) => {
    const found = team.find((member) => member.id === id);
    const name = found ? `${found.firstname} ${found.lastname}`.trim() : '';
    return id === meId ? text('me', { name }) : name;
  };
  const toggle = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

  const radio = (key, value, label) => (
    <StyledInput
      key={value}
      type="radio"
      name={`companies-${key}`}
      label={label}
      checked={filters[key] === value}
      onChange={() => onChange({ [key]: value })}
    />
  );

  const renderFilterByKey = (key) => {
    if (key === 'departments') {
      const chosen = departmentList.filter((item) => item.checked).map((item) => item.label);
      return (
        <StyledInput
          type="selectMultipleCheckbox"
          list={departmentList}
          value={chosen.length ? chosen.join(', ') : text('pickDepartment')}
          onChange={(list) => onChange({ departments: list.filter((item) => item.checked).map((item) => item.value) })}
          isSearchable
          showChevron
          showCheck
          dropdownFullWidth
        />
      );
    }
    if (key === 'category') {
      return (
        <div className="flex flex-col gap-3">
          {radio('category', '', text('allCategories'))}
          {categories.map((category) => radio('category', String(category.id), category.label))}
        </div>
      );
    }
    if (key === 'suppliers') {
      return (
        <div className="flex flex-col gap-3">
          {supplierList.map((option) => (
            <StyledInput
              key={option.value}
              type="checkbox"
              label={option.label}
              checked={filters.suppliers.includes(option.value)}
              onChange={() => onChange({ suppliers: toggle(filters.suppliers, option.value) })}
            />
          ))}
        </div>
      );
    }
    if (key === 'missionCount') {
      return (
        <div className="flex flex-col gap-3">
          {radio('missionCount', 'never', text('missionsNone'))}
          {radio('missionCount', 'at_least', text('missionsAtLeast'))}
          {filters.missionCount === 'at_least' && (
            <StyledInput
              type="number"
              label={text('missionsMin')}
              value={filters.missionCountMin}
              onChange={(event) => onChange({ missionCountMin: event.target.value })}
            />
          )}
        </div>
      );
    }
    if (key === 'incompleteRemuneration') {
      return (
        <StyledInput
          type="checkbox"
          label={text('payIncomplete')}
          detail={text('payIncompleteDetail')}
          checked={filters.incompleteRemuneration}
          onChange={() => onChange({ incompleteRemuneration: !filters.incompleteRemuneration })}
        />
      );
    }
    if (key === 'action') {
      return (
        <div className="flex flex-col gap-3">
          {radio('action', 'with', text('actionWith'))}
          {radio('action', 'without', text('actionWithout'))}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {commercialsOf(team).map((member) => radio('commercial', member.id, person(member.id)))}
      </div>
    );
  };

  const displayValue = (key) => {
    switch (key) {
      case 'departments':
        return filters.departments.length
          ? departmentList.filter((item) => item.checked).map((item) => item.label).join(', ')
          : null;
      case 'category':
        return filters.category
          ? categories.find((category) => String(category.id) === filters.category)?.label ?? filters.category
          : null;
      case 'suppliers':
        return filters.suppliers.length
          ? supplierList.filter((option) => filters.suppliers.includes(option.value)).map((option) => option.label).join(', ')
          : null;
      case 'missionCount':
        if (filters.missionCount === 'never') return text('missionsNone');
        return filters.missionCount === 'at_least' && filters.missionCountMin
          ? text('missionsAtLeastCount', { count: filters.missionCountMin })
          : null;
      case 'incompleteRemuneration':
        return filters.incompleteRemuneration ? text('payIncomplete') : null;
      case 'action':
        return filters.action === 'all' ? null : text(filters.action === 'with' ? 'actionWith' : 'actionWithout');
      default:
        return filters.commercial === 'all' ? null : person(filters.commercial);
    }
  };

  const CLEARED = {
    departments: { departments: [] },
    category: { category: '' },
    suppliers: { suppliers: [] },
    missionCount: { missionCount: '', missionCountMin: '' },
    incompleteRemuneration: { incompleteRemuneration: false },
    action: { action: 'all' },
    commercial: { commercial: 'all' },
  };

  return (
    <FilterPanel
      isOpen={isOpen}
      onClose={onClose}
      activeFilterCount={activeFilterCount(filters)}
      filters={[
        { key: 'departments', label: text('filterDepartment') },
        ...(categories.length ? [{ key: 'category', label: text('filterCategory') }] : []),
        { key: 'action', label: text('filterAction') },
        { key: 'commercial', label: text('filterCommercial') },
        { key: 'suppliers', label: text('filterSupplier') },
        { key: 'missionCount', label: text('filterMissions') },
        { key: 'incompleteRemuneration', label: text('filterPay') },
      ]}
      renderFilterByKey={renderFilterByKey}
      getFilterDisplayValue={displayValue}
      clearFilterByKey={(key) => onChange(CLEARED[key])}
      onReset={() => onChange(NO_FILTERS)}
      onApply={onClose}
    />
  );
};

export default CompanyFilterPanel;
